import { useCallback, useEffect, useId, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '@parrot/utils';
import { useAnchoredPosition, type Side } from '../internal/anchor';
import styles from './Tooltip.module.css';

/* Room for the arrow plus a hairline of breathing space. */
const GAP = 10;

/* Only ever called on an element that may or may not be open, and jsdom implements
   neither method — so both are guarded rather than tracked with a second piece of state
   that could disagree with the popover itself. */
const openPopover = (element: HTMLElement | null) => {
  if (element === null || typeof element.showPopover !== 'function') return;
  if (!element.matches(':popover-open')) element.showPopover();
};

const closePopover = (element: HTMLElement | null) => {
  if (element === null || typeof element.hidePopover !== 'function') return;
  if (element.matches(':popover-open')) element.hidePopover();
};

type TooltipProps = Omit<HTMLAttributes<HTMLSpanElement>, 'content' | 'title'> & {
  /** The body. Text, or something close to it — this is not a container for UI. */
  content: ReactNode;
  /** Optional heading above the content, set uppercase. */
  title?: ReactNode;
  /** Which side of the trigger it prefers. It flips when there isn't room. */
  placement?: Side;
  /** How long the pointer must rest before it opens. Closing is always immediate. */
  delay?: number;
  /** Renders the trigger untouched, with no tooltip attached. */
  disabled?: boolean;
  /** The trigger. */
  children: ReactNode;
};

/**
 * A label that appears on hover or focus and explains its trigger.
 *
 * Text only, and never interactive: a tooltip you can put a button in is a popover, and
 * the moment it holds something worth clicking it also has to be dismissible, focusable
 * and keyboard-navigable. Menu is that component.
 *
 * Uncontrolled, like Menu and unlike the other composites. The rule exists so a parent
 * can refuse a change, and there is nothing to refuse about a pointer resting on
 * something.
 *
 * Rendered in the top layer via `popover`, so a tooltip inside a table with
 * `overflow: hidden` isn't clipped and needs no z-index. `manual` rather than `auto`
 * deliberately: an auto popover light-dismisses every other one when it opens, so a
 * tooltip on a menu item would close the menu underneath it.
 */
export const Tooltip = ({
  content,
  title,
  placement = 'top',
  delay = 300,
  disabled = false,
  children,
  className,
  ...spanProps
}: TooltipProps) => {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const getAnchor = useCallback(() => anchorRef.current, []);
  useAnchoredPosition({ elementRef: tipRef, getAnchor, side: placement, gap: GAP });

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    closePopover(tipRef.current);
  }, []);

  const show = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => openPopover(tipRef.current), delay);
  }, [delay]);

  /* A manual popover doesn't get the platform's Escape handling, and dismissing a
     tooltip is the one thing a keyboard user will try. Bound to the window because focus
     may be on the trigger, and a pointer user has focus nowhere near it. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hide]);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (disabled) return children;

  return (
    <>
      <span
        ref={anchorRef}
        className={cx(styles.trigger, className)}
        // Free from wrapping anyway, and it's what lets a test find the tip from its
        // trigger rather than by reaching for the popover element directly.
        aria-describedby={id}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={show}
        onBlur={hide}
        {...spanProps}
      >
        {children}
      </span>

      <div id={id} ref={tipRef} popover={'manual'} role={'tooltip'} className={styles.tip}>
        <span className={styles.arrow} aria-hidden={'true'} />
        {title !== undefined && <p className={styles.title}>{title}</p>}
        <p className={styles.content}>{content}</p>
      </div>
    </>
  );
};
